import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  CircularProgress,
} from "@mui/material";
import { Search as SearchIcon } from "@mui/icons-material";
import toast from "react-hot-toast";
import PageContentLayout from "../layout/PageContentLayout";

/**
 * Reusable school list table for billed / not-billed / expired views.
 */
const SchoolStatusTable = ({
  title,
  subtitle,
  breadcrumbLabel,
  breadcrumbIcon,
  backTo = "/schools",
  banner,
  fetchRows,
  columns,
  getRowKey = (row) => row.school_code,
  searchFields = (row) => [row.school_name, row.admin_name, row.state, row.country],
  onRowClick,
}) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    const result = await fetchRows();
    if (result.success) {
      setRows(Array.isArray(result.data) ? result.data : []);
    } else {
      toast.error(result.error || "Failed to load schools");
      setRows([]);
    }
    setLoading(false);
  }, [fetchRows]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = rows.filter((row) => {
    const haystack = searchFields(row).filter(Boolean).join(" ").toLowerCase();
    return haystack.includes(searchTerm.toLowerCase());
  });

  return (
    <PageContentLayout
      title={title}
      subtitle={subtitle}
      barSubtitle={`${rows.length} school${rows.length === 1 ? "" : "s"}`}
      breadcrumbs={[{ label: breadcrumbLabel, icon: breadcrumbIcon }]}
      backTo={backTo}
      darkBar
    >
      {banner}
      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ py: 1.5 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search schools..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </CardContent>
      </Card>
      <Card>
        <CardContent sx={{ p: 0 }}>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress size={32} />
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {columns.map((col) => (
                      <TableCell key={col.id} align={col.align || "left"}>
                        {col.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filtered.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={columns.length} align="center" sx={{ py: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          No schools found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filtered.map((row) => (
                      <TableRow
                        key={getRowKey(row)}
                        hover
                        onClick={onRowClick ? () => onRowClick(row) : undefined}
                        sx={onRowClick ? { cursor: "pointer" } : undefined}
                      >
                        {columns.map((col) => (
                          <TableCell key={col.id} align={col.align || "left"}>
                            {col.render(row)}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </PageContentLayout>
  );
};

export default SchoolStatusTable;
